/**
 * The four "execution at" settings (#51-#54) are CLOCK TIMES, not durations.
 *
 * The Python V2 port assumed they were integers - minutes since midnight - and shipped a pair of
 * helpers that converted both ways on that assumption. They are `java.time.LocalTime` on the entity
 * (`AppRunTimerSettings.startExecutionAtMin` and siblings) over a MySQL `TIME` column, and the API
 * sends `LocalTime.toString()`, i.e. "23:55" (or "23:55:30" if seconds are ever set).
 *
 * That mismatch broke both directions:
 *
 *   READ   parseInt("23:55", 10) -> 23 -> rendered "00:23". A slot stored as 00:16/00:17/23:55/23:55
 *          displayed as 00:00/00:00/00:23/00:23. Every slot, every load.
 *   WRITE  "23:50" -> 23*60+50 -> 1430 -> LocalTime.parse("1430") -> DateTimeParseException, 400,
 *          and the settings write is all-or-nothing, so NOTHING on the page saved.
 *
 * The second is the nastier one: correcting the wrong time on screen is the exact action that makes
 * the whole page unsaveable.
 *
 * These fields are the only ones the page treats as times, so there is no minutes-since-midnight
 * case left to support - the helpers below normalise, they do not convert.
 */

/**
 * A server value -> the string `<input type="time">` wants.
 *
 * Accepts "H:mm", "HH:mm" and "HH:mm:ss" (all shapes `LocalTime.toString()` can produce) and
 * returns "HH:mm". Anything it cannot read becomes '' - an empty time input, which is honest about
 * not knowing rather than inventing midnight.
 */
export function toTimeInput(value) {
  if (value === undefined || value === null || value === '') return '';
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?/.exec(String(value).trim());
  if (!m) return '';
  const hours = Number(m[1]);
  const mins = Number(m[2]);
  if (!(hours >= 0 && hours <= 23) || !(mins >= 0 && mins <= 59)) return '';
  return `${String(hours).padStart(2, '0')}:${m[2]}`;
}

/**
 * What the time input produced -> what goes on the wire, in the shape `LocalTime.parse` accepts.
 *
 * Returns null for an empty or unreadable input, and the caller must then leave the setting
 * untouched. Neither alternative is available: '' throws DateTimeParseException (400, whole save
 * lost), and the "null" sentinel that clears numeric settings is NOT safe here - it was measured
 * turning a 400 into a 500 on LocalTime fields, which is why `SettingsMetadataIndex.clearsOnEmpty`
 * is restricted to Integer and Long.
 */
export function fromTimeInput(text) {
  const normalised = toTimeInput(text);
  return normalised === '' ? null : normalised;
}
